export enum PerformanceEventName {
  AcquireTokenForResource = 'acquire_token_for_resource',
  ApiFetch = 'api_fetch',
  AzureVideoLoad = 'azure_video_load',
  AuthBootstrap = 'auth_bootstrap',
  AuthPageLoad = 'auth_page_load',
  Bootstrap = 'bootstrap',
  BootstrapBundleLoad = 'bootstrap_bundle_load',
  BootstrapNavigation = 'bootstrap_navigation',
  CumulativeLayoutShift = 'cumulative_layout_shift',
  FileAttach = 'file_attach',
  FileBrowserLoad = 'file_browser_load',
  FilePickerLoad = 'file_picker_load',
  FileUpload = 'file_upload',
  FirstContentfulPaint = 'first_contentful_paint',
  FirstInputDelay = 'first_input_delay',
  FirstMeaningfulPaint = 'first_meaningful_paint',
  FirstPaint = 'first_paint',
  ImageLoad = 'image_load',
  LargestContentfulPaint = 'largest_contentful_paint',
  LoadTeamsMeetingDetails = 'load_teams_meeting_details',
  ManagedApiAction = 'managed_api_action',
  MessagePost = 'message_post',
  MomentHomeCarouselLoad = 'moment_home_carousel_load',
  MomentsNavigation = 'moments_navigation',
  MomentUserCarouselLoad = 'moment_user_carousel_load',
  PerceivedImageLoad = 'perceived_image_load',
  RelayFetch = 'relay_fetch',
  RendererBundleLoad = 'renderer_bundle_load',
  ResourceTiming = 'resource_timing',
  ServiceWorkerRegistration = 'service_worker_registration',
  ServiceWorkerDisable = 'service_worker_disable',
  SharePointAuthTokenFetch = 'sharepoint_auth_token_fetch',
  SharePointWebPartConfiguration = 'webpart_config',
  TeamsLiveEventVideoLoad = 'teams_liveevent_video_load',
  TimeToFirstByte = 'time_to_first_byte',
  UiComponentMeasure = 'ui_component_measure',
}

export interface BootstrapPerformanceEventProperties {
  readonly endedWithError: boolean;
}

export interface NavigationPerformanceEventProperties extends Record<string, number> {
  readonly fetchStart: number;
  readonly responseEnd: number;
  readonly navigationStart: number;
  readonly connectEnd: number;
  readonly connectStart: number;
  readonly domainLookupStart: number;
  readonly domainLookupEnd: number;
  readonly domComplete: number;
  readonly domContentLoadedEventEnd: number;
  readonly domContentLoadedEventStart: number;
  readonly domInteractive: number;
  readonly loadEventEnd: number;
  readonly loadEventStart: number;
  readonly redirectEnd: number;
  readonly redirectStart: number;
  readonly requestStart: number;
  readonly responseStart: number;
  readonly unloadEventEnd: number;
  readonly unloadEventStart: number;
  readonly workerStart: number;
}

type ResourceTimingProperties = Omit<PerformanceResourceTiming, 'toJSON'>;

export interface ResourcePerformanceEventProperties extends ResourceTimingProperties, Record<string, string | number> {
  readonly name: string;
}

export interface BundleResourcePerformanceEventProperties extends ResourcePerformanceEventProperties {
  readonly bundleName: string;
}

export interface GraphqlResourcePerformanceEventProperties extends ResourcePerformanceEventProperties {
  readonly operationName: string;
  readonly requestId: string;
}

export interface FirstPaintPerformanceEventProperties {
  readonly isMeasurementApproximated: boolean;
}

export interface UiComponentPerformanceEventProperties {
  readonly componentMeasureName: string;
}

export interface FeedUiComponentPerformanceEventProperties extends UiComponentPerformanceEventProperties {
  readonly feedType: string;
}

export interface ApiActionPerformanceEventProperties {
  readonly apiName: string;
  readonly actionName?: string;
  readonly actionType?: string;
  readonly cacheExists?: boolean;
  readonly cachingStrategy?: string;
  readonly endedWithError: boolean;
  readonly message?: string;
  readonly reason?: string;
  readonly idempotencyToken?: string;
  readonly requestId: string;
  readonly initiator?: string;
  readonly afdRef?: string;
  readonly cacheHit?: boolean;
}

export interface AcquireTokenPerformanceEventProperties {
  readonly scopes: string;
}

export interface RenderPerformanceEventProperties {
  readonly endedAfterStyleAndLayout: boolean;
}

export interface FileAttachPerformanceEventProperties {
  readonly isSmallFileUpload: boolean;
}

export interface FileUploadPerformanceEventProperties {
  readonly storageType: string;
}

export interface FilePickerPageLoadPerformanceEventProperties {
  readonly errorCode?: string;
  readonly isExpectedError?: boolean;
  readonly message?: string;
}

export interface ScenarioPerformanceEventProperties {
  readonly endedWithError: boolean;
  readonly timedOut: boolean;
  readonly interrupted: boolean;
}

export interface ImageLoadPerformanceEventProperties {
  readonly imageOrigin: string;
  readonly imageType: string;
  readonly isGif?: boolean;
  readonly isTenor?: boolean;
  readonly imageSurface?: string;
  readonly isSignedImage?: boolean;
  readonly isPartlyCollapsedAttachmentsLayout?: boolean;
}

export interface AzureVideoPlayerPerformanceEventProperties {
  readonly videoId: string;
  readonly groupId?: string;
  readonly errorCode?: string;
}

export interface FirstMeaningfulPaintPerformanceEventProperties {
  readonly urlRootPath: string;
}

export interface PropertyCheckPerformanceEventProperties {
  readonly droppedKeys: string;
}

export interface TeamsLiveEventVideoPerformanceEventProperties {
  readonly broadcastId?: string;
  readonly type?: string;
}

export interface StoriesCarouselPerformanceEventProperties {
  readonly loadTimeStamps?: string[];
  readonly navigationStatus?: string;
  readonly navigationScenario?: string;
  readonly triggerType?: string;
  readonly itemSurface?: string;
  readonly toItemType?: string;
  readonly momentExperienceScenario?: string;
  readonly modalAlreadyOpen?: boolean;
}

export type PerformanceEventProperties =
  | { [key: string]: never }
  | Pick<AzureVideoPlayerPerformanceEventProperties, keyof AzureVideoPlayerPerformanceEventProperties>
  | Pick<BootstrapPerformanceEventProperties, keyof BootstrapPerformanceEventProperties>
  | Pick<NavigationPerformanceEventProperties, keyof NavigationPerformanceEventProperties>
  | Pick<ResourcePerformanceEventProperties, keyof ResourcePerformanceEventProperties>
  | Pick<FirstPaintPerformanceEventProperties, keyof FirstPaintPerformanceEventProperties>
  | Pick<UiComponentPerformanceEventProperties, keyof UiComponentPerformanceEventProperties>
  | Pick<ApiActionPerformanceEventProperties, keyof ApiActionPerformanceEventProperties>
  | Pick<AcquireTokenPerformanceEventProperties, keyof AcquireTokenPerformanceEventProperties>
  | Pick<PropertyCheckPerformanceEventProperties, keyof PropertyCheckPerformanceEventProperties>
  | Pick<RenderPerformanceEventProperties, keyof RenderPerformanceEventProperties>
  | Pick<FeedUiComponentPerformanceEventProperties, keyof FeedUiComponentPerformanceEventProperties>
  | Pick<FileAttachPerformanceEventProperties, keyof FileAttachPerformanceEventProperties>
  | Pick<FileUploadPerformanceEventProperties, keyof FileUploadPerformanceEventProperties>
  | Pick<FilePickerPageLoadPerformanceEventProperties, keyof FilePickerPageLoadPerformanceEventProperties>
  | Pick<ScenarioPerformanceEventProperties, keyof ScenarioPerformanceEventProperties>
  | Pick<ImageLoadPerformanceEventProperties, keyof ImageLoadPerformanceEventProperties>
  | Pick<FirstMeaningfulPaintPerformanceEventProperties, keyof FirstMeaningfulPaintPerformanceEventProperties>
  | Pick<TeamsLiveEventVideoPerformanceEventProperties, keyof TeamsLiveEventVideoPerformanceEventProperties>
  | Pick<StoriesCarouselPerformanceEventProperties, keyof StoriesCarouselPerformanceEventProperties>;

export type ScenarioEventName = PerformanceEventName.FirstMeaningfulPaint;

export interface PerformanceMeasure {
  readonly eventName: PerformanceEventName;
  readonly measureKey: string;
}

export type MeasureNamedPerformanceEvent =
  | PerformanceEventName.UiComponentMeasure
  | PerformanceEventName.ManagedApiAction
  | PerformanceEventName.ApiFetch
  | PerformanceEventName.RelayFetch;

export type NamedPerformanceEvent = Exclude<PerformanceEventName, MeasureNamedPerformanceEvent>;
